import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ImportPdkHoldersWizard from "./ImportPdkHoldersWizard";
import ExportUsersToPdkWizard from "./ExportUsersToPdkWizard";
import PdkReconcileModal from "./PdkReconcileModal";
import PdkSyncStatusModal from "./PdkSyncStatusModal";
import { EntioDataGrid } from "src/components/ui";
import { GridColDef } from "@mui/x-data-grid";
import message from "src/components/Message";
import { formatDate } from "src/utils/common";
import {
  acsIntegrationClient,
  AcsDoorLinkRow,
  AcsImportedDoor,
  AcsIntegrationRow,
  PdkHolderCustomFieldKey,
  PdkReconcilePreview,
} from "src/apis/acsIntegrationClient";
import pdkLogo from "src/assets/pdk-logo.png";
import { buildingServiceFind, apartmentServiceFind } from "src/apis";
import { useAppSelector } from "src/redux/hooks";
import { useLicenseQuota } from "src/hooks/useLicenseQuota";
import { isPdkLicenseQuotaError } from "src/hooks/usePdkResidentLock";

export type AcsProviderTab = "PDK" | "BRIVO";

type BuildingLite = { id: string; name: string; apartmentId?: string };
type ApartmentLite = { id: string; name: string };

type Props = {
  provider: AcsProviderTab;
};

export function AcsProviderTabPanel({ provider: p }: Props) {
  const account = useAppSelector((s) => s.account);
  const { isInsufficient, usedUnits, totalUnits } = useLicenseQuota();
  const [licenseReminderOpen, setLicenseReminderOpen] = useState(false);
  const orgId = account?.userDetail?.organizations?.[0]?.id as
    | string
    | undefined;
  const [loading, setLoading] = useState(false);
  const [acsLoading, setAcsLoading] = useState(false);
  const [integrations, setIntegrations] = useState<AcsIntegrationRow[]>([]);
  const [doors, setDoors] = useState<AcsImportedDoor[]>([]);
  const [links, setLinks] = useState<AcsDoorLinkRow[]>([]);
  const [virtualIntercoms, setVirtualIntercoms] = useState<
    { id: string; name: string }[]
  >([]);
  const [pdkForm, setPdkForm] = useState({ systemId: "" });
  const [brivoForm, setBrivoForm] = useState({
    clientId: "",
    clientSecret: "",
  });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkDoor, setLinkDoor] = useState<AcsImportedDoor | null>(null);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [intercomId, setIntercomId] = useState("");
  const [testTarget, setTestTarget] = useState<AcsDoorLinkRow | null>(null);
  const [testInFlight, setTestInFlight] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [exportWizardOpen, setExportWizardOpen] = useState(false);
  const [buildings, setBuildings] = useState<BuildingLite[]>([]);
  const [apartments, setApartments] = useState<ApartmentLite[]>([]);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileMode, setReconcileMode] = useState<"enable" | "disable">(
    "enable",
  );
  const [reconcilePreview, setReconcilePreview] =
    useState<PdkReconcilePreview | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileSubmitting, setReconcileSubmitting] = useState(false);
  const [unitFieldOptions, setUnitFieldOptions] = useState<
    PdkHolderCustomFieldKey[] | null
  >(null);
  const [unitFieldLoading, setUnitFieldLoading] = useState(false);
  const [syncStatusOpen, setSyncStatusOpen] = useState(false);

  const integration = useMemo(
    () => integrations.find((i) => i.provider === p) || null,
    [integrations, p],
  );
  const doorsForProvider = useMemo(
    () => doors.filter((d) => d.provider === p),
    [doors, p],
  );
  const linksForProvider = useMemo(
    () => links.filter((l) => l.door?.provider === p),
    [links, p],
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, dRes, lRes, vRes] = await Promise.all([
        acsIntegrationClient.listIntegrations(),
        acsIntegrationClient.listImportedDoors(),
        acsIntegrationClient.listDoorLinks(),
        acsIntegrationClient.listVirtualIntercoms(),
      ]);
      if (iRes.data.success) setIntegrations(iRes.data.data || []);
      if (dRes.data.success) setDoors(dRes.data.data || []);
      if (lRes.data.success) setLinks(lRes.data.data || []);
      if (vRes.data.success) setVirtualIntercoms(vRes.data.data || []);
    } catch (e) {
      console.error(e);
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!orgId || p !== "PDK") return;
    let cancelled = false;
    (async () => {
      try {
        const [bRes, aRes] = await Promise.all([
          buildingServiceFind(orgId, undefined, undefined),
          apartmentServiceFind(orgId, undefined, undefined, undefined),
        ]);
        if (cancelled) return;
        const bsRaw =
          (
            bRes as unknown as {
              data?: {
                buildings?: Array<BuildingLite & { apartment_id?: string }>;
              };
            }
          )?.data?.buildings || [];
        const apsRaw = (
          aRes as unknown as { data?: { apartments?: ApartmentLite[] } }
        )?.data?.apartments;
        const aps = Array.isArray(apsRaw)
          ? apsRaw
          : Array.isArray((aRes as unknown as { data?: ApartmentLite[] })?.data)
            ? (aRes as unknown as { data: ApartmentLite[] }).data
            : [];
        setBuildings(
          bsRaw.map((b) => ({
            id: String(b.id),
            name: b.name,
            apartmentId: b.apartmentId
              ? String(b.apartmentId)
              : b.apartment_id
                ? String(b.apartment_id)
                : undefined,
          })),
        );
        setApartments(aps.map((a) => ({ id: String(a.id), name: a.name })));
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, p]);

  useEffect(() => {
    // Field choices come from the live holder list; reset when the integration changes.
    setUnitFieldOptions(null);
  }, [integration?.id]);

  const loadUnitFieldOptions = async () => {
    if (!integration || unitFieldOptions !== null || unitFieldLoading) return;
    try {
      setUnitFieldLoading(true);
      const res = await acsIntegrationClient.listPdkHolderCustomFields(
        integration.id,
      );
      if (res.data.success && res.data.data) {
        setUnitFieldOptions(res.data.data.fields || []);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        err?.response?.data?.message || "Failed to load PDK custom fields",
      );
      setUnitFieldOptions([]);
    } finally {
      setUnitFieldLoading(false);
    }
  };

  const saveUnitSource = async (customField: string) => {
    if (!integration) return;
    try {
      setAcsLoading(true);
      const res = await acsIntegrationClient.setHolderUnitSource(
        integration.id,
        { customField: customField || null },
      );
      if (res.data.success && res.data.data) {
        setIntegrations((prev) =>
          prev.map((i) => (i.id === res.data.data.id ? res.data.data : i)),
        );
        message.success(
          customField
            ? `Units will be read from PDK field “${customField}” when the standard unit is empty`
            : "Units will be read from PDK's standard unit only",
        );
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        err?.response?.data?.message || "Failed to save PDK unit field",
      );
    } finally {
      setAcsLoading(false);
    }
  };

  const toggleHolderAutoSync = async (enabled: boolean) => {
    if (!integration) return;

    try {
      setAcsLoading(true);
      setReconcileLoading(true);
      setReconcileMode(enabled ? "enable" : "disable");
      setReconcilePreview(null);

      const previewRes = await acsIntegrationClient.previewHolderReconcile(
        integration.id,
      );
      if (!previewRes.data.success || !previewRes.data.data) {
        message.error("Failed to compare PDK and Entrio");
        return;
      }
      const preview = previewRes.data.data;

      if (!enabled) {
        const hasDeletes =
          !preview.deletesDisabled && preview.deletes.length > 0;
        if (!hasDeletes) {
          const res = await acsIntegrationClient.applyHolderReconcile(
            integration.id,
            { enabled: false, selections: { create: [], update: [], delete: [] } },
          );
          if (res.data.success && res.data.data) {
            setIntegrations((prev) =>
              prev.map((i) => (i.id === res.data.data.id ? res.data.data : i)),
            );
            message.success("PDK people auto-sync disabled");
          }
          return;
        }
      }

      setReconcilePreview(preview);
      setReconcileOpen(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        err?.response?.data?.message || "Failed to prepare auto-sync reconcile",
      );
    } finally {
      setAcsLoading(false);
      setReconcileLoading(false);
    }
  };

  const confirmReconcile = async (payload: {
    selections: {
      create: Array<{
        holderId: string;
        apply: boolean;
        apartmentId: string;
        buildingId: string;
        unitNumber: string;
      }>;
      update: Array<{ holderId: string; apply: boolean }>;
      delete: Array<{ holderId: string; apply: boolean }>;
    };
  }) => {
    if (!integration) return;
    try {
      setReconcileSubmitting(true);
      const res = await acsIntegrationClient.applyHolderReconcile(
        integration.id,
        {
          enabled: reconcileMode === "enable",
          selections: payload.selections,
        },
      );
      if (res.data.success && res.data.data) {
        setIntegrations((prev) =>
          prev.map((i) => (i.id === res.data.data.id ? res.data.data : i)),
        );
        message.success(
          reconcileMode === "enable"
            ? "PDK people auto-sync enabled"
            : "PDK people auto-sync disabled",
        );
        setReconcileOpen(false);
        setReconcilePreview(null);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        err?.response?.data?.message || "Failed to apply reconcile",
      );
    } finally {
      setReconcileSubmitting(false);
    }
  };

  const connectPdk = async () => {
    if (!pdkForm.systemId.trim()) return;
    try {
      setAcsLoading(true);
      await acsIntegrationClient.connect({
        provider: "PDK",
        pdkSystemId: pdkForm.systemId.trim(),
      });
      message.success("PDK connected and doors synced");
      setPdkForm({ systemId: "" });
      await loadAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || "PDK connect failed");
    } finally {
      setAcsLoading(false);
    }
  };

  const connectBrivo = async () => {
    if (!brivoForm.clientId.trim() || !brivoForm.clientSecret) {
      message.error("Brivo API client ID and secret are required");
      return;
    }
    try {
      setAcsLoading(true);
      await acsIntegrationClient.connect({
        provider: "BRIVO",
        clientId: brivoForm.clientId.trim(),
        clientSecret: brivoForm.clientSecret,
      });
      message.success("Brivo connected and doors synced");
      setBrivoForm({ clientId: "", clientSecret: "" });
      await loadAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || "Brivo connect failed");
    } finally {
      setAcsLoading(false);
    }
  };

  const syncAcs = async (id: string) => {
    try {
      setAcsLoading(true);
      await acsIntegrationClient.syncDoors(id);
      message.success("Doors refreshed from provider");
      await loadAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || "Sync failed");
    } finally {
      setAcsLoading(false);
    }
  };

  const deleteAcs = async (id: string) => {
    try {
      setAcsLoading(true);
      await acsIntegrationClient.deleteIntegration(id);
      message.success("Integration removed");
      await loadAll();
    } catch {
      message.error("Delete failed");
    } finally {
      setAcsLoading(false);
    }
  };

  const closeLinkDialog = () => {
    setLinkOpen(false);
    setEditingLinkId(null);
    setLinkDoor(null);
    setIntercomId("");
  };

  const openLink = (row: AcsImportedDoor) => {
    if (row.hasLink) {
      message.warning("This door is already linked to a virtual intercom");
      return;
    }
    setEditingLinkId(null);
    setLinkDoor(row);
    setIntercomId("");
    setLinkOpen(true);
  };

  const openEditLink = (row: AcsImportedDoor) => {
    const lk = linksForProvider.find((l) => l.importedDoorId === row.id);
    if (!lk) {
      message.error("Could not find this link. Try refreshing the page.");
      return;
    }
    setLinkDoor(row);
    setEditingLinkId(lk.id);
    setIntercomId(lk.intercomDeviceId);
    setLinkOpen(true);
  };

  const submitLink = async () => {
    if (!linkDoor || !intercomId) {
      message.error("Select a virtual intercom");
      return;
    }
    try {
      if (editingLinkId) {
        const existing = links.find((l) => l.id === editingLinkId);
        if (existing?.intercomDeviceId === intercomId) {
          message.info("No changes to save");
          closeLinkDialog();
          return;
        }
        await acsIntegrationClient.deleteDoorLink(editingLinkId);
        await acsIntegrationClient.createDoorLink({
          importedDoorId: linkDoor.id,
          intercomDeviceId: intercomId,
        });
        message.success("Intercom link updated");
      } else {
        await acsIntegrationClient.createDoorLink({
          importedDoorId: linkDoor.id,
          intercomDeviceId: intercomId,
        });
        message.success("Door linked to virtual intercom");
      }
      closeLinkDialog();
      await loadAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        err?.response?.data?.message ||
          (editingLinkId ? "Update failed" : "Link failed"),
      );
    }
  };

  const removeLink = async (id: string) => {
    try {
      await acsIntegrationClient.deleteDoorLink(id);
      message.success("Link removed");
      await loadAll();
    } catch {
      message.error("Failed to remove link");
    }
  };

  const confirmTestOpen = async () => {
    if (!testTarget) return;
    setTestInFlight(true);
    try {
      await acsIntegrationClient.testDoorLink(testTarget.id);
      message.success(
        `Sent unlock to ${testTarget.door?.displayName || "door"}`,
      );
      setTestTarget(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || "Test open failed");
    } finally {
      setTestInFlight(false);
    }
  };

  const doorColumns: GridColDef[] = [
    { field: "displayName", headerName: "Door", flex: 1, minWidth: 160 },
    {
      field: "linkedIntercom",
      headerName: "Intercom",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (c) => {
        const row = c.row as AcsImportedDoor;
        const lk = linksForProvider.find((l) => l.importedDoorId === row.id);
        const label = lk?.intercomName || lk?.intercomDeviceId;
        if (!label) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Typography variant="body2" noWrap title={label}>
            {label}
          </Typography>
        );
      },
    },
    {
      field: "lastSyncedAt",
      headerName: "Last sync",
      width: 180,
      renderCell: (c) => (c.value ? formatDate(c.value) : "—"),
    },
    {
      field: "hasLink",
      headerName: "Linked",
      width: 100,
      renderCell: (c) => (
        <Chip
          size="small"
          label={c.value ? "Yes" : "No"}
          color={c.value ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 200,
      minWidth: 200,
      sortable: false,
      renderCell: (c) => {
        const row = c.row as AcsImportedDoor;
        if (!row.hasLink) {
          return (
            <Button
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => openLink(row)}
            >
              Link
            </Button>
          );
        }
        const lk = linksForProvider.find((l) => l.importedDoorId === row.id);
        return (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ py: 0.5 }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => openEditLink(row)}
            >
              Edit
            </Button>
            <IconButton
              size="small"
              color="error"
              disabled={!lk}
              onClick={() => lk && void removeLink(lk.id)}
              aria-label="Remove door link"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const pdkConnectCard = p === "PDK" && (
    <Stack spacing={1.5} sx={{ maxWidth: 480 }}>
      <TextField
        size="small"
        fullWidth
        label="System ID"
        placeholder="e.g. 4c914894-070e-4a3a-9088-9a32db2a1907"
        value={pdkForm.systemId}
        onChange={(e) =>
          setPdkForm((f) => ({ ...f, systemId: e.target.value }))
        }
      />
      <Typography variant="body2" color="text.secondary">
        PDK 2.0 uses a System ID (UUID), which can be found in System Settings
        within the{" "}
        <Link href="https://pdk.io" target="_blank" rel="noopener noreferrer">
          PDK Portal
        </Link>
        .
      </Typography>
      <Button
        variant="contained"
        onClick={() => void connectPdk()}
        disabled={acsLoading || !pdkForm.systemId.trim()}
        sx={{ alignSelf: "flex-start" }}
      >
        Add integration
      </Button>
    </Stack>
  );

  const brivoConnectCard = p === "BRIVO" && (
    <Stack spacing={1.5} sx={{ maxWidth: 480 }}>
      <TextField
        size="small"
        fullWidth
        label="API client ID"
        value={brivoForm.clientId}
        onChange={(e) =>
          setBrivoForm((f) => ({ ...f, clientId: e.target.value }))
        }
      />
      <TextField
        size="small"
        fullWidth
        type="password"
        label="API client secret"
        value={brivoForm.clientSecret}
        onChange={(e) =>
          setBrivoForm((f) => ({ ...f, clientSecret: e.target.value }))
        }
      />
      <Typography variant="body2" color="text.secondary">
        Use credentials from your Brivo developer application. The server may
        require <code>ACS_CREDENTIALS_KEY</code> to store the secret.
      </Typography>
      <Button
        variant="contained"
        onClick={() => void connectBrivo()}
        disabled={acsLoading}
        sx={{ alignSelf: "flex-start" }}
      >
        Add integration
      </Button>
    </Stack>
  );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          {p === "PDK" && (
            <Box
              component="img"
              src={pdkLogo}
              alt="Prodatakey (PDK)"
              sx={{
                height: 40,
                width: "auto",
                maxWidth: 160,
                display: "block",
                objectFit: "contain",
              }}
            />
          )}
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {p === "PDK" ? "Prodatakey (PDK)" : "Brivo"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, maxWidth: 720 }}
            >
              {p === "PDK"
                ? "Add your PDK system, import doors, then link each door to a virtual intercom for unlocks."
                : "Add the Brivo access API, import doors, then link each door to a virtual intercom."}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void loadAll()}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {integration ? (
        <>
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ "&:last-child": { pb: 2 } }}>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              spacing={1}
            >
              <Chip size="small" label="Connected" color="success" />
              {p === "PDK" && integration.configJson?.systemId && (
                <Typography variant="body2" color="text.secondary">
                  System ID: {integration.configJson.systemId}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                color="error"
                onClick={() => void deleteAcs(integration.id)}
                disabled={acsLoading}
              >
                Remove integration
              </Button>
            </Stack>
            {integration.lastError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {integration.lastError}
              </Typography>
            )}
          </CardContent>
        </Card>

        {p === "PDK" && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography variant="h6">Automatic people sync</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Keeps Entrio residents in step with PDK as people change.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FactCheckOutlinedIcon />}
                  onClick={() => setSyncStatusOpen(true)}
                  sx={{ flexShrink: 0 }}
                >
                  View sync status
                </Button>
              </Stack>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(
                        integration.configJson?.holderAutoSyncEnabled,
                      )}
                      disabled={acsLoading}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setLicenseReminderOpen(true);
                          return;
                        }
                        void toggleHolderAutoSync(false);
                      }}
                    />
                  }
                  label="Auto-sync people from PDK"
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5, maxWidth: 720 }}
                >
                  A new PDK person is created in Entrio when they have an email
                  and their unit matches exactly one Entrio room; otherwise they
                  are skipped (see View sync status for why). Name, email, phone,
                  and unit stay in sync, and deleting a person in PDK removes the
                  Entrio resident. While this is on, add, remove, or rename
                  residents in pdk.io. Unit assignment can still be changed in
                  Entrio.
                </Typography>
                {(isInsufficient ||
                  integration.configJson?.holderAutoSyncLicenseBlocked ||
                  isPdkLicenseQuotaError(
                    integration.configJson?.webhookLastError,
                    integration.configJson?.webhookLastErrorCode,
                  )) && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    Not enough unit licenses. New people added in PDK will not
                    be created in Entrio until you add licenses on the License
                    page. Existing synced residents still update. The person
                    remains in PDK.
                  </Alert>
                )}
                <TextField
                  select
                  size="small"
                  label="PDK unit field"
                  value={integration.configJson?.unitCustomField || ""}
                  disabled={acsLoading}
                  sx={{ minWidth: 280, maxWidth: 420 }}
                  helperText={
                    unitFieldLoading
                      ? "Loading custom fields from PDK…"
                      : integration.configJson?.unitCustomField
                        ? `Standard PDK unit first; “${integration.configJson.unitCustomField}” when it is empty.`
                        : "Only PDK's standard unit assignment is used. Pick a custom field if this system types the unit there."
                  }
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    onOpen: () => void loadUnitFieldOptions(),
                    displayEmpty: true,
                  }}
                  onChange={(e) => void saveUnitSource(String(e.target.value))}
                >
                  <MenuItem value="">PDK standard unit only</MenuItem>
                  {integration.configJson?.unitCustomField &&
                    !(unitFieldOptions || []).some(
                      (f) => f.key === integration.configJson?.unitCustomField,
                    ) && (
                      <MenuItem value={integration.configJson.unitCustomField}>
                        {integration.configJson.unitCustomField}
                      </MenuItem>
                    )}
                  {(unitFieldOptions || []).map((f) => (
                    <MenuItem key={f.key} value={f.key}>
                      {f.key}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        {f.count} {f.count === 1 ? "person" : "people"}
                        {f.sample ? ` · e.g. ${f.sample}` : ""}
                      </Typography>
                    </MenuItem>
                  ))}
                  {unitFieldOptions !== null &&
                    unitFieldOptions.length === 0 && (
                      <MenuItem disabled value="__none">
                        No custom fields found on PDK people
                      </MenuItem>
                    )}
                </TextField>
                {integration.configJson?.webhookLastError && (
                  <Typography
                    variant="caption"
                    color="error"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {integration.configJson.webhookLastError}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {p === "PDK" && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6">Manual people transfer</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5, maxWidth: 720 }}
              >
                One-time copies between PDK and Entrio. Use these to load people
                in bulk or push Entrio residents into PDK; they do not keep
                anything in sync.
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                <Tooltip
                  title={
                    integration.configJson?.holderAutoSyncEnabled
                      ? "Off while automatic sync is on — it imports people for you."
                      : ""
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PeopleAltOutlinedIcon />}
                      onClick={() => setImportWizardOpen(true)}
                      disabled={
                        acsLoading ||
                        Boolean(integration.configJson?.holderAutoSyncEnabled)
                      }
                    >
                      Import people from PDK
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileOutlinedIcon />}
                  onClick={() => setExportWizardOpen(true)}
                  disabled={acsLoading}
                >
                  Export users to PDK
                </Button>
                {integration.configJson?.holderAutoSyncEnabled && (
                  <Typography variant="caption" color="text.secondary">
                    Import is handled by automatic sync while it is on.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}
        </>
      ) : (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Connect
            </Typography>
            {p === "PDK" ? pdkConnectCard : brivoConnectCard}
          </CardContent>
        </Card>
      )}

      {integration && (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Intercom door links
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Each virtual intercom can be linked to one {p} door. Linking
                enables remote unlock for that intercom.
              </Typography>
              {linksForProvider.length === 0 ? (
                <Typography color="text.secondary">No links yet</Typography>
              ) : (
                <Stack spacing={1}>
                  {linksForProvider.map((lk) => (
                    <Stack
                      key={lk.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography fontWeight={600}>
                          {lk.door?.displayName} —{" "}
                          {lk.intercomName || lk.intercomDeviceId}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LockOpenIcon />}
                          onClick={() => setTestTarget(lk)}
                        >
                          Test open
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => void removeLink(lk.id)}
                          aria-label="remove link"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="h6">Imported doors</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={() => void syncAcs(integration.id)}
                  disabled={acsLoading}
                >
                  Import / sync doors
                </Button>
              </Stack>
              <EntioDataGrid
                loading={loading}
                rows={doorsForProvider}
                getRowId={(r) => r.id}
                columns={doorColumns}
                pageSizeOptions={[5, 10, 25]}
                emptyMessage="No doors"
                emptyDescription="Click Import / sync doors, or check your PDK or Brivo system."
                sx={{ minWidth: 520 }}
              />
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={linkOpen} onClose={closeLinkDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingLinkId
            ? "Change intercom link"
            : "Link door to virtual intercom"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {linkDoor?.displayName} ({p})
          </Typography>
          <FormControl fullWidth size="small" variant="outlined" sx={{ mt: 1 }}>
            <InputLabel>Choose intercom</InputLabel>
            <Select
              value={intercomId}
              label="Choose intercom"
              onChange={(e) => setIntercomId(e.target.value)}
            >
              {virtualIntercoms.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name || d.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLinkDialog}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitLink()}>
            {editingLinkId ? "Save" : "Link"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!testTarget}
        onClose={() => !testInFlight && setTestTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Test open door</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will physically unlock{" "}
            <strong>{testTarget?.door?.displayName || "this door"}</strong>{" "}
            right now, using the same flow as the intercom button. The door will
            re-lock automatically after the configured Strike Time.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Linked intercom:{" "}
            {testTarget?.intercomName || testTarget?.intercomDeviceId}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestTarget(null)} disabled={testInFlight}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void confirmTestOpen()}
            disabled={testInFlight}
          >
            {testInFlight ? "Opening…" : "Open now"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={licenseReminderOpen}
        onClose={() => setLicenseReminderOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Before enabling PDK auto-sync</DialogTitle>
        <DialogContent>
          <Alert severity={isInsufficient ? "warning" : "info"} sx={{mb: 2}}>
            {isInsufficient
              ? totalUnits > 0
                ? `You do not have enough unit licenses right now (${usedUnits} of ${totalUnits} used). New people synced from PDK may fail to create in Entrio until you add licenses.`
                : "You do not have enough unit licenses right now. New people synced from PDK may fail to create in Entrio until you add licenses."
              : "Make sure you have enough unit licenses for new PDK people. If quota runs out, creates from PDK will fail until you add licenses on the License page."}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Existing linked residents can still update. Failed creates stay in
            PDK only and are not added to Entrio. You can add licenses later and
            retry by updating the person in pdk.io or re-running reconcile.
          </Typography>
          {totalUnits > 0 && !isInsufficient && (
            <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
              Current usage: {usedUnits} of {totalUnits} unit licenses used.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLicenseReminderOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={isInsufficient ? "warning" : "primary"}
            onClick={() => {
              setLicenseReminderOpen(false);
              void toggleHolderAutoSync(true);
            }}
          >
            {isInsufficient ? "Continue anyway" : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      <ImportPdkHoldersWizard
        open={importWizardOpen && p === "PDK"}
        integrationId={p === "PDK" && integration ? integration.id : null}
        onClose={() => setImportWizardOpen(false)}
        onCompleted={() => void loadAll()}
      />

      <ExportUsersToPdkWizard
        open={exportWizardOpen && p === "PDK"}
        integrationId={p === "PDK" && integration ? integration.id : null}
        onClose={() => setExportWizardOpen(false)}
        onCompleted={() => void loadAll()}
      />

      {p === "PDK" && (
        <PdkSyncStatusModal
          open={syncStatusOpen}
          integrationId={integration?.id ?? null}
          onClose={() => setSyncStatusOpen(false)}
        />
      )}
      <PdkReconcileModal
        open={reconcileOpen && p === "PDK"}
        mode={reconcileMode}
        integrationId={p === "PDK" && integration ? integration.id : null}
        preview={reconcilePreview}
        loading={reconcileLoading}
        submitting={reconcileSubmitting}
        apartments={apartments}
        buildings={buildings}
        onClose={() => {
          if (reconcileSubmitting) return;
          setReconcileOpen(false);
          setReconcilePreview(null);
        }}
        onConfirm={(payload) => void confirmReconcile(payload)}
      />
    </Box>
  );
}
